import { createReducer } from '@reduxjs/toolkit';

import { createEntity } from '../lib';

import {
  changeFilterAction,
  changePaginationAction,
  changeSearchAction,
  changeSearchLocationAction,
  changeSelectedTypeAction,
  changeViewModeAction,
  loadFiltersValues,
  loadProjectsSpecific,
  resetFilterAction,
  setSelectedRecommendedAction,
  setAllCarbonProjectsAction,
  setCurrentDataLayerType,
  setCurrentSkippedItems,
  setCurrentGeoRasterData,
  setRecommendationPagination,
  setSelectedRecommendationTypeAction,
  setMangroveViewModeAction,
} from './actions';
import { projectsInitialState, ProjectsState } from './state';
import { TRecommendedProjectData, RecommendationPagination, TCarbonProjectData } from '../../types';
import { ProjectFilters, ProjectPagination, GeoRasterData, FilterValuesPayload } from './actions';

export const projectsReducer = createReducer<ProjectsState>(projectsInitialState, (builder) => {
  builder.addCase(loadProjectsSpecific.submit, (state) => {
    state.recommended = createEntity(state.recommended.data, true);
    state.forestation = createEntity(state.forestation.data, true);
    state.carbon = createEntity(state.carbon.data, true);
    state.favorite = createEntity(state.favorite.data, true);
  });

  builder.addCase(loadProjectsSpecific.success, (state, { payload }) => {
    if (payload.recommended) {
      state.recommended = createEntity(payload.recommended);
    }
    if (payload.forestation) {
      if (Array.isArray(payload.forestation)) {
        // Handle legacy array format
        state.forestation = createEntity({
          items: payload.forestation,
          total: payload.forestation.length,
        });
      } else if (
        payload.forestation &&
        typeof payload.forestation === 'object' &&
        'items' in payload.forestation &&
        Array.isArray((payload.forestation as any).items)
      ) {
        // Handle new object format with items and total
        const forestationData = payload.forestation as any;
        state.forestation = createEntity({
          items: forestationData.items,
          total: forestationData.total || forestationData.items.length,
        });
      } else {
        // Ensure forestation loading state is cleared
        state.forestation = createEntity(state.forestation.data);
      }
    } else {
      // Ensure forestation loading state is cleared even if no data is provided
      state.forestation = createEntity(state.forestation.data);
    }
    if (payload.carbon) {
      state.carbon = createEntity(payload.carbon);
    }
    if (payload.favorite) {
      state.favorite = createEntity(payload.favorite);
    }
  });

  builder.addCase(loadProjectsSpecific.fail, (state, { payload }) => {
    state.recommended = createEntity(
      {
        items: [],
        total: { all: 0, carbon_projects: 0, recommendations: 0, searched: 0, saved: 0 },
        pagination: { total: 0 },
      },
      false,
      payload,
    );
    state.forestation = createEntity(
      {
        items: [],
        total: 0,
      },
      false,
      payload,
    );
    state.carbon = createEntity(
      {
        items: [],
        total: { all: 0, carbon_projects: 0, recommendations: 0, searched: 0, saved: 0 },
      },
      false,
      payload,
    );
    state.favorite = createEntity(state.favorite.data, false, payload);
  });

  builder.addCase(loadFiltersValues.submit, (state) => {
    state.filterValues = createEntity(
      {
        methodologies: [],
        types: [],
        max_estimated_annual_emission_reduction: 999999999,
      },
      true,
    );
  });

  builder.addCase(
    loadFiltersValues.success,
    (state, { payload }: { payload: FilterValuesPayload }) => {
      state.filterValues = createEntity(payload);
    },
  );

  builder.addCase(loadFiltersValues.fail, (state, { payload }) => {
    state.filterValues = createEntity(
      {
        methodologies: [],
        types: [],
        max_estimated_annual_emission_reduction: 999999999,
      },
      false,
      payload,
    );
  });

  builder.addCase(
    changeFilterAction,
    (state, { payload: filter }: { payload: Partial<ProjectFilters> }) => {
      state.filters = { ...state.filters, ...filter };
    },
  );

  builder.addCase(
    changePaginationAction,
    (state, { payload: pagination }: { payload: Partial<ProjectPagination> }) => {
      state.pagination = { ...state.pagination, ...pagination };
    },
  );

  builder.addCase(resetFilterAction, (state) => {
    state.filters = { ...projectsInitialState.filters };
    state.search = generateNull();
    state.searchByNaming = projectsInitialState.searchByNaming;
    state.pagination = projectsInitialState.pagination;
  });

  builder.addCase(
    changeSelectedTypeAction,
    (state, { payload }: { payload: 'recommendation' | 'current' | 'saved' }) => {
      state.selected = payload;
      state.pagination = projectsInitialState.pagination;
    },
  );

  builder.addCase(
    setSelectedRecommendationTypeAction,
    (state, { payload }: { payload: string }) => {
      state.selectedRecommendationType = payload as any;
    },
  );

  builder.addCase(
    changeSearchLocationAction,
    (state, { payload }: { payload: { level: number; id: string; name: string } | null }) => {
      state.search = payload;
    },
  );

  builder.addCase(changeSearchAction, (state, { payload }: { payload: string }) => {
    state.searchByNaming = payload;
  });

  builder.addCase(changeViewModeAction, (state, { payload }: { payload: 'list' | 'grid' }) => {
    state.viewMode = payload;
  });

  builder.addCase(
    setSelectedRecommendedAction,
    (
      state,
      {
        payload,
      }: {
        payload: TRecommendedProjectData | undefined;
      },
    ) => {
      state.selectedRecommendedProject = payload;
    },
  );

  builder.addCase(
    setAllCarbonProjectsAction,
    (state, { payload }: { payload: Array<TCarbonProjectData> }) => {
      state.allCarbonProjects = payload;
    },
  );

  builder.addCase(setCurrentDataLayerType, (state, { payload }: { payload: string }) => {
    state.currentDataLayerType = payload;
  });

  builder.addCase(setCurrentSkippedItems, (state, { payload }: { payload: number }) => {
    state.currentSkippedItems = payload;
  });

  builder.addCase(setCurrentGeoRasterData, (state, { payload }: { payload: GeoRasterData }) => {
    state.currentGeoRasterData = payload;
  });

  builder.addCase(
    setRecommendationPagination,
    (state, { payload }: { payload: RecommendationPagination }) => {
      state.recommendationPagination = payload;
    },
  );

  builder.addCase(
    setMangroveViewModeAction,
    (state, { payload }: { payload: 'districts' | 'aggregations' }) => {
      state.mangroveViewMode = payload;
    },
  );
});

const generateNull = (): null => null;
